import { Dispatch, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../utils/axios";

export enum MembershipStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
  SUSPENDED = "suspended",
}

export interface UserAssociation {
  id: number;
  userId: number;
  associationId: number;
  roles: string[];
  status: MembershipStatus;
  joinedAt: string;
  leftAt?: string;
  user?: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    isProtected?: boolean;
    isSuperAdmin?: boolean;
    protectionReason?: string;
  };
  association?: {
    id: number;
    name: string;
  };
}

export interface UserAssociationState {
  userAssociations: UserAssociation[];
  associationMembers: UserAssociation[];
  currentMembership?: UserAssociation;
  status: "idle" | "loading" | "failed";
  error?: string;
}

const initialState: UserAssociationState = {
  userAssociations: [],
  associationMembers: [],
  currentMembership: undefined,
  status: "idle",
  error: undefined,
};

/**
 * Fetch all associations for a user
 */
export const fetchUserAssociations = createAsyncThunk(
  "userAssociation/fetchUserAssociations",
  async (userId: number, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/users/${userId}/associations`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Fetch all members of an association
 */
export const fetchAssociationMembers = createAsyncThunk(
  "userAssociation/fetchAssociationMembers",
  async (
    {
      associationId,
      activeOnly = false,
    }: { associationId: number; activeOnly?: boolean },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.get(
        `/associations/${associationId}/members?activeOnly=${activeOnly}`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Fetch specific user-association membership
 */
export const fetchUserAssociation = createAsyncThunk(
  "userAssociation/fetchUserAssociation",
  async (
    { userId, associationId }: { userId: number; associationId: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.get(
        `/associations/${associationId}/members/${userId}`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Add user to association
 */
export const addUserToAssociation = createAsyncThunk(
  "userAssociation/addUserToAssociation",
  async (
    {
      associationId,
      userId,
      roles = ["MEMBER"],
      status = MembershipStatus.ACTIVE,
    }: {
      associationId: number;
      userId: number;
      roles?: string[];
      status?: MembershipStatus;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post(
        `/associations/${associationId}/members`,
        { userId, roles, status }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Update user-association membership
 */
export const updateUserAssociation = createAsyncThunk(
  "userAssociation/updateUserAssociation",
  async (
    {
      associationId,
      userId,
      updates,
    }: {
      associationId: number;
      userId: number;
      updates: { roles?: string[]; status?: MembershipStatus };
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.patch(
        `/associations/${associationId}/members/${userId}`,
        updates
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Update user roles in association
 */
export const updateUserRoles = createAsyncThunk(
  "userAssociation/updateUserRoles",
  async (
    {
      associationId,
      userId,
      roles,
    }: {
      associationId: number;
      userId: number;
      roles: string[];
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.patch(
        `/associations/${associationId}/members/${userId}/roles`,
        { roles }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Remove user from association
 */
export const removeUserFromAssociation = createAsyncThunk(
  "userAssociation/removeUserFromAssociation",
  async (
    { associationId, userId }: { associationId: number; userId: number },
    { rejectWithValue }
  ) => {
    try {
      await axiosInstance.delete(
        `/associations/${associationId}/members/${userId}`
      );
      return { associationId, userId };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Reactivate user membership
 */
export const reactivateUserMembership = createAsyncThunk(
  "userAssociation/reactivateUserMembership",
  async (
    { associationId, userId }: { associationId: number; userId: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post(
        `/associations/${associationId}/members/${userId}/reactivate`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Suspend user membership
 */
export const suspendUserMembership = createAsyncThunk(
  "userAssociation/suspendUserMembership",
  async (
    { associationId, userId }: { associationId: number; userId: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post(
        `/associations/${associationId}/members/${userId}/suspend`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const userAssociationSlice = createSlice({
  name: "userAssociation",
  initialState,
  reducers: {
    setUserAssociations(state, action) {
      state.userAssociations = action.payload;
    },
    setAssociationMembers(state, action) {
      state.associationMembers = action.payload;
    },
    addUserAssociation(state, action) {
      state.associationMembers.push(action.payload);
    },
    updateUserAssociationLocal(state, action) {
      const index = state.associationMembers.findIndex(
        (ua) =>
          ua.userId === action.payload.userId &&
          ua.associationId === action.payload.associationId
      );
      if (index !== -1) {
        state.associationMembers[index] = {
          ...state.associationMembers[index],
          ...action.payload,
        };
      }
    },
    removeUserAssociationLocal(state, action) {
      state.associationMembers = state.associationMembers.filter(
        (ua) =>
          !(
            ua.userId === action.payload.userId &&
            ua.associationId === action.payload.associationId
          )
      );
    },
    clearError(state) {
      state.error = undefined;
    },
  },
  extraReducers: (builder) => {
    // Fetch user associations
    builder.addCase(fetchUserAssociations.pending, (state) => {
      state.status = "loading";
    });
    builder.addCase(fetchUserAssociations.fulfilled, (state, action) => {
      state.userAssociations = action.payload;
      state.status = "idle";
    });
    builder.addCase(fetchUserAssociations.rejected, (state, action) => {
      state.status = "failed";
      state.error = action.payload as string;
    });

    // Fetch association members
    builder.addCase(fetchAssociationMembers.pending, (state) => {
      state.status = "loading";
    });
    builder.addCase(fetchAssociationMembers.fulfilled, (state, action) => {
      state.associationMembers = action.payload;
      state.status = "idle";
    });
    builder.addCase(fetchAssociationMembers.rejected, (state, action) => {
      state.status = "failed";
      state.error = action.payload as string;
    });

    // Fetch specific membership
    builder.addCase(fetchUserAssociation.pending, (state) => {
      state.status = "loading";
    });
    builder.addCase(fetchUserAssociation.fulfilled, (state, action) => {
      state.currentMembership = action.payload;
      state.status = "idle";
    });
    builder.addCase(fetchUserAssociation.rejected, (state, action) => {
      state.status = "failed";
      state.error = action.payload as string;
    });

    // Add user to association
    builder.addCase(addUserToAssociation.fulfilled, (state, action) => {
      state.associationMembers.push(action.payload);
    });

    // Update user association
    builder.addCase(updateUserAssociation.fulfilled, (state, action) => {
      const index = state.associationMembers.findIndex(
        (ua) => ua.id === action.payload.id
      );
      if (index !== -1) {
        state.associationMembers[index] = action.payload;
      }
    });

    // Update user roles
    builder.addCase(updateUserRoles.fulfilled, (state, action) => {
      const index = state.associationMembers.findIndex(
        (ua) => ua.id === action.payload.id
      );
      if (index !== -1) {
        state.associationMembers[index] = action.payload;
      }
    });

    // Remove user from association
    builder.addCase(removeUserFromAssociation.fulfilled, (state, action) => {
      state.associationMembers = state.associationMembers.filter(
        (ua) =>
          !(
            ua.userId === action.payload.userId &&
            ua.associationId === action.payload.associationId
          )
      );
    });

    // Reactivate membership
    builder.addCase(reactivateUserMembership.fulfilled, (state, action) => {
      const index = state.associationMembers.findIndex(
        (ua) => ua.id === action.payload.id
      );
      if (index !== -1) {
        state.associationMembers[index] = action.payload;
      }
    });

    // Suspend membership
    builder.addCase(suspendUserMembership.fulfilled, (state, action) => {
      const index = state.associationMembers.findIndex(
        (ua) => ua.id === action.payload.id
      );
      if (index !== -1) {
        state.associationMembers[index] = action.payload;
      }
    });
  },
});

export const {
  setUserAssociations,
  setAssociationMembers,
  addUserAssociation,
  updateUserAssociationLocal,
  removeUserAssociationLocal,
  clearError,
} = userAssociationSlice.actions;

export default userAssociationSlice.reducer;
